import React, { useCallback, useMemo } from 'react';
import { gql } from '@apollo/react-hooks';
import type { ActionFn, Action, DataGridProps, SetIsLoading } from '#/common/components/DataGrid';
import useToast from '#/common/hooks/useToast';
import useSession from '#/common/hooks/useSession';
import DataGrid from '#/common/components/DataGrid';
import { getManufacturerList } from '#/graphql/queries';
import { Manufacturer } from '#/graphql/types';
import useManufacturerDelete from '../../hooks/useDeleteManufacturerMutation';
import columns from './columns';
import { actions } from './actions';
import { UserRole } from '#/common/types/userTypes';
import { inUseStatuses } from '../../utils/constants';

const getManufacturerListQuery = gql(getManufacturerList);

const userSelectedActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['delete-manufacturer'],
  [UserRole.trane_admin]: ['delete-manufacturer'],
  [UserRole.technician]: [],
};

const gridActionsPerRole: Record<UserRole, string[]> = {
  [UserRole.asset_manager]: ['new'],
  [UserRole.trane_admin]: ['new'],
  [UserRole.technician]: [],
};

const gridOptionsPerRole: Record<UserRole, Partial<DataGridProps<Manufacturer>>> = {
  [UserRole.asset_manager]: { selectionMode: 'multiple' },
  [UserRole.trane_admin]: { selectionMode: 'multiple' },
  [UserRole.technician]: { selectionMode: 'none' },
};

const ManufacturerDataGrid: React.FC = () => {
  const { confirm } = useToast();
  const { user } = useSession();
  const { deleteManufacturer } = useManufacturerDelete();

  const role = user?.role as UserRole | undefined;

  const deleteItem = useCallback(
    async (ids: string[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!ids) {
        return undefined;
      }
      const sure = await confirm('error', `Are you sure you want to delete these manufacturers?`);
      if (!sure) {
        return;
      }

      setIsLoading && setIsLoading(true, 'Deleting data...');
      await deleteManufacturer(ids.join(','));
      setIsLoading && setIsLoading(false);
    },
    [deleteManufacturer],
  );

  const userSelectedActions: ActionFn<Manufacturer>[] = useMemo(() => {
    const res: ActionFn<Manufacturer>[] = [];

    if (role && userSelectedActionsPerRole[role]?.includes('delete-manufacturer')) {
      res.push({
        key: 'delete-manufacturer',
        icon: 'trash',
        text: 'Delete',
        disabled: (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (manufacturer) =>
              manufacturer.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(manufacturer.thirdPartyId && manufacturer.thirdPartySource),
          );
          let count = filteredItemsForDelete?.length;
          return count < 1;
        },
        onClick: async (e) => {
          const filteredItemsForDelete = e.selectedItems.filter(
            (manufacturer) =>
              manufacturer.inUseStatus === inUseStatuses.newlyCreated.key &&
              !(manufacturer.thirdPartyId && manufacturer.thirdPartySource),
          );

          if (filteredItemsForDelete.length < e.selectedItems.length) {
            const sure = await confirm(
              'error',
              `Some selected manufacturers are not in 'Newly Created' status and can not be deleted.`,
            );
            if (!sure) {
              return;
            }
          }

          await deleteItem(
            filteredItemsForDelete.map((manufacturer) => manufacturer.id!),
            { setIsLoading: e.setIsLoading },
          );
          e.dg?.instance.clearSelection();
          e.dg?.instance.refresh();
        },
      });
    }
    return res;
  }, [role]);

  const gridActions = useMemo<Action<Manufacturer>[]>(() => {
    return actions
      .filter((a) => typeof a !== 'object' || (role && gridActionsPerRole[role]?.includes(a?.key)))
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };
        return result;
      })
      .filter((a) => !!a);
  }, [role]);

  if (!role) return null;

  return (
    <DataGrid
      id="manufacturers"
      pluralText="Manufacturers"
      singularText="Manufacturer"
      columns={columns}
      recordUrl="/assets/manufacturers/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getManufacturerListQuery}
      {...gridOptionsPerRole[role]}
    />
  );
};

export default React.memo(ManufacturerDataGrid);
